import { useEffect, useMemo, useState, useRef } from 'react';
import { apiFetch } from '../api';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useSidebar } from './SidebarContext';
import { loadPermissions, hasPermissionSync } from '../auth/permissions';
import { SmartcardWidget } from './SmartcardWidget';
import { useChat } from '../contexts/ChatContext';
import { PAGE_REGISTRY, PAGES_BY_CODE, type PageDef } from '../nav/pages';

// keep PAGE_REGISTRY imported so unused-warning is avoided in some builds
void PAGE_REGISTRY;

interface NavModule {
  id: string;
  code: string;
  name: string;
  icon: string | null;
  color: string | null;
  sort_order: number;
  required_org_module: string | null;
  required_permission: string | null;
  required_org_modules?: string[];
  required_permissions?: string[];
  page_codes: string[];
}

interface NavConfig {
  modules: NavModule[];
  enabledOrgModules: string[];
  isSuperAdmin: boolean;
  permissions: string[];
  roles: string[];
}

const ACTIVE_MODULE_KEY = 'sidebar_active_module';

function LinkItem({ to, children, collapsed, onClick, external }: { to: string; children: React.ReactNode; collapsed?: boolean; onClick?: () => void; external?: boolean }) {
  const location = useLocation();
  const isLinkActive = (linkTo: string) => (location.pathname + location.hash) === linkTo;
  const cls = `${collapsed ? 'flex items-center justify-center px-2 py-1.5' : 'flex items-center gap-3 px-3 py-2.5 sm:py-2'} rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700hover:bg-gray-700 transition`;

  if (external) {
    return (
      <button
        type="button"
        onClick={() => {
          const token = localStorage.getItem('token');
          const sep = to.includes('?') ? '&' : '?';
          window.open(`${to}${sep}token=${token}`, '_blank');
          onClick?.();
        }}
        className={`w-full text-left ${cls}`}
      >
        {children}
      </button>
    );
  }

  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={() => `${cls} ${isLinkActive(to) ? 'bg-blue-50 dark:bg-gray-700 text-blue-600 dark:text-blue-400 font-semibold shadow-xs' : ''}`}
    >
      {children}
    </NavLink>
  );
}

function PageRow({ page, collapsed, onClick }: { page: PageDef; collapsed: boolean; onClick: () => void }) {
  const icon = typeof page.icon === 'function' ? (page.icon as any)() : page.icon;
  const isExternal = page.to.startsWith('external:') || page.external;
  const to = page.to.startsWith('external:') ? page.to.slice('external:'.length) : page.to;
  const { unreadCount } = useChat();

  return (
    <LinkItem to={to} collapsed={collapsed} onClick={onClick} external={isExternal}>
      <span className="w-8 h-8 flex items-center justify-center flex-shrink-0 relative text-lg">
        {icon}
        {page.code === 'chat' && unreadCount > 0 && collapsed && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full text-[8px] font-bold w-4 h-4 flex items-center justify-center animate-pulse">
            {unreadCount}
          </span>
        )}
      </span>
      {!collapsed && (
        <span className="text-sm text-gray-700 dark:text-gray-300 truncate flex-grow flex justify-between items-center">
          <span className="truncate">{page.label}</span>
          {page.code === 'chat' && unreadCount > 0 && (
            <span className="bg-red-500 text-white rounded-full text-[10px] font-bold px-2 py-0.5 leading-none mr-1">
              {unreadCount}
            </span>
          )}
        </span>
      )}
    </LinkItem>
  );
}

export default function Sidebar() {
  const navigate = useNavigate();
  const { collapsed, setCollapsed, mobileOpen, setMobileOpen } = useSidebar();
  const [config, setConfig] = useState<NavConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeModuleCode, setActiveModuleCode] = useState<string>(() => {
    try { return localStorage.getItem(ACTIVE_MODULE_KEY) || 'home'; } catch { return 'home'; }
  });
  const token = localStorage.getItem('token');
  const location = useLocation();

  useEffect(() => {
    if (!token) { setConfig(null); setLoading(false); return; }
    setLoading(true);
    Promise.all([loadPermissions(true), apiFetch('/nav/config')])
      .then(([_, cfg]: any[]) => { setConfig(cfg); })
      .catch(err => { console.error('[Sidebar] nav/config failed', err); setConfig({ modules: [], enabledOrgModules: [], isSuperAdmin: false, permissions: [], roles: [] }); })
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    try { localStorage.setItem(ACTIVE_MODULE_KEY, activeModuleCode); } catch {}
  }, [activeModuleCode]);

  const ctx = useMemo(() => ({
    enabledOrgModules: new Set(config?.enabledOrgModules || []),
    isSuperAdmin: !!config?.isSuperAdmin,
  }), [config]);

  function pageVisible(p: PageDef): boolean {
    if (!p) return false;
    if (ctx.isSuperAdmin) return true;
    if (p.requiresOrgModule && !ctx.enabledOrgModules.has(p.requiresOrgModule)) return false;
    if (p.requiredPermission && !hasPermissionSync(p.requiredPermission)) return false;
    if (p.isVisible && !p.isVisible(ctx)) return false;
    return true;
  }

  const visibleModules = useMemo(() => {
    if (!config) return [];
    return config.modules.filter(m => {
      if (ctx.isSuperAdmin) return true;

      // Module-Freigabeprüfung (OR-Semantik, wie im Backend)
      if (m.required_org_modules && m.required_org_modules.length > 0) {
        if (!m.required_org_modules.some(code => ctx.enabledOrgModules.has(code))) return false;
      } else if (m.required_org_module && !ctx.enabledOrgModules.has(m.required_org_module)) {
        return false;
      }

      // Berechtigungsprüfung (OR-Semantik, wie im Backend)
      if (m.required_permissions && m.required_permissions.length > 0) {
        if (!m.required_permissions.some(perm => hasPermissionSync(perm))) return false;
      } else if (m.required_permission && !hasPermissionSync(m.required_permission)) {
        return false;
      }

      const anyPage = m.page_codes.some(code => {
        const p = PAGES_BY_CODE[code];
        return p && pageVisible(p);
      });
      return anyPage;
    });
  }, [config, ctx]);

  // Automatische Modulauswahl passend zur aktuellen URL, falls aktiv
  useEffect(() => {
    if (visibleModules.length === 0) return;
    const currentPath = location.pathname + location.hash;
    const matchingModule = visibleModules.find(m =>
      m.page_codes.some(code => {
        const p = PAGES_BY_CODE[code];
        if (!p) return false;
        const targetTo = p.to.startsWith('external:') ? p.to.slice('external:'.length) : p.to;
        return currentPath === targetTo || (targetTo !== '/' && currentPath.startsWith(targetTo));
      })
    );
    if (matchingModule && matchingModule.code !== activeModuleCode) {
      setActiveModuleCode(matchingModule.code);
    }
  }, [location.pathname, location.hash, visibleModules]);

  const activeModule = useMemo(() => {
    return visibleModules.find(m => m.code === activeModuleCode) || visibleModules[0] || null;
  }, [visibleModules, activeModuleCode]);

  const activePages = useMemo(() => {
    if (!activeModule) return [] as PageDef[];
    return activeModule.page_codes
      .map(code => PAGES_BY_CODE[code])
      .filter((p): p is PageDef => !!p && pageVisible(p));
  }, [activeModule, ctx]);

  // Schließen per Escape-Taste: schließt Navigation vollständig
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setMobileOpen]);

  // Bei echtem Routenwechsel (Seitenwechsel) mobile Navigation schließen
  const lastPathRef = useRef(location.pathname);
  useEffect(() => {
    if (lastPathRef.current !== location.pathname) {
      lastPathRef.current = location.pathname;
      if (typeof window !== 'undefined' && window.innerWidth < 1024) {
        setMobileOpen(false);
      }
    }
  }, [location.pathname, setMobileOpen]);

  return (
    <>
      {/* Mobiler Backdrop (bzw. Overlay auf mobilen Bildschirmen) */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-xs z-40 lg:hidden transition-opacity"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar Drawer */}
      <aside
        className={`
          w-72 sm:w-80 lg:${collapsed ? 'w-20' : 'w-72'}
          bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700
          h-full min-h-screen fixed top-0 left-0 z-50 flex flex-col shadow-2xl lg:shadow-none
          transition-transform duration-300 ease-in-out
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Sidebar Header */}
        <div className="px-4 py-3 flex items-center justify-between border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0 shadow-xs">
              <LogoInline small={collapsed} />
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <div className="text-base font-bold text-gray-900 dark:text-whitetext-white truncate">OrgaPilot</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{activeModule?.name || 'Hauptmenü'}</div>
              </div>
            )}
          </div>

          <div className="flex items-center">
            {/* Schließen Button (✕) - schließt Sidebar vollständig auf Desktop und Mobil */}
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-center p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:text-white dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700hover:bg-gray-700 transition"
              aria-label="Menü schließen"
              title="Menü schließen (ESC)"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Aktive Seitenliste des gewählten Moduls */}
        <div className="flex-1 overflow-y-auto px-3 py-3">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-2xl animate-pulse">⏳</div>
          ) : activePages.length === 0 ? (
            <div className="text-xs text-gray-500 dark:text-gray-400 px-3 py-4 text-center">
              {collapsed && !mobileOpen ? '…' : 'Keine Seiten in diesem Modul.'}
            </div>
          ) : (
            <nav className="flex flex-col gap-1">
              {activePages.map(p => (
                <PageRow key={p.code} page={p} collapsed={collapsed && !mobileOpen} onClick={() => setMobileOpen(false)} />
              ))}
            </nav>
          )}
        </div>

        {/* Modulauswahl-Bereich unten */}
        <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/60 px-3 py-3 flex-shrink-0">
          {(!collapsed || mobileOpen) && (
            <div className="text-[11px] uppercase tracking-wide font-semibold text-gray-400 dark:text-gray-500 dark:text-gray-400 px-1 mb-2">Module</div>
          )}
          <div className={`grid ${collapsed && !mobileOpen ? 'grid-cols-1' : 'grid-cols-3'} gap-2 max-h-[232px] overflow-y-auto pr-1`}>
            {visibleModules.map(m => {
              const isActive = m.code === activeModule?.code;
              const color = m.color || '#3B82F6';
              return (
                <button
                  key={m.code}
                  type="button"
                  onClick={() => {
                    setActiveModuleCode(m.code);
                    if (m.code === 'hotel') {
                      navigate('/hotel');
                      if (typeof window !== 'undefined' && window.innerWidth < 1024) setMobileOpen(false);
                    }
                  }}
                  title={m.name}
                  className={`relative flex flex-col items-center justify-center rounded-xl px-1.5 py-2.5 border transition shadow-xs cursor-pointer
                    ${isActive
                      ? 'bg-white dark:bg-gray-800 shadow-md ring-2'
                      : 'bg-white dark:bg-gray-800/70 dark:bg-gray-800/80 border-gray-200 dark:border-gray-700 hover:bg-white dark:bg-gray-800hover:bg-gray-700'}
                  `}
                  style={isActive ? { ringColor: color, borderColor: color } : undefined}
                >
                  <ModuleIcon icon={m.icon} color={color} />
                  {(!collapsed || mobileOpen) && (
                    <span className="mt-1 text-[10px] sm:text-[11px] text-center font-medium text-gray-700 dark:text-gray-300 line-clamp-2 leading-tight">{m.name}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {!loading && (!collapsed || mobileOpen) && ctx.isSuperAdmin && (
          <div className="border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 flex-shrink-0">
            <SmartcardWidget compact={false} showError={true} />
          </div>
        )}
      </aside>

      {/* Desktop-Platzhalter, damit der Hauptinhalt nicht unter die Sidebar rutscht, nur wenn Sidebar geöffnet ist */}
      {mobileOpen && (
        <div className={`hidden lg:block ${collapsed ? 'w-20' : 'w-72'} flex-shrink-0 transition-all duration-300`} />
      )}
    </>
  );
}

function ModuleIcon({ icon, color }: { icon: string | null; color: string }) {
  if (!icon) return <span className="text-xl" style={{ color }}>●</span>;
  if (icon.startsWith('/') || icon.startsWith('http')) {
    return <img src={icon} alt="" className="w-6 h-6 object-contain" />;
  }
  return <span className="text-xl" style={{ color }}>{icon}</span>;
}

function LogoInline({ small }: { small?: boolean }) {
  try {
    const org = localStorage.getItem('organization') || 'VO';
    const key = `orgLogo_${org}`;
    const data = localStorage.getItem(key);
    if (data) return <img src={data} alt="logo" className={`${small ? 'w-8 h-8' : 'w-full h-full'} object-contain`} />;
    const initials = (org || 'VO').slice(0, 2).toUpperCase();
    return <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">{initials}</div>;
  } catch {
    return <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">VO</div>;
  }
}
